import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { referralsService } from '../services/rewardsService';
import { useAuth } from '../contexts/AuthContext';
import { isIOS } from '../services/platform.service';
import {
  Dumbbell,
  UserPlus,
  User,
  Mail,
  Lock,
  ShieldCheck,
  Eye,
  EyeOff,
  ArrowLeft,
  CheckCircle,
  Building2,
  Gift,
} from 'lucide-react';

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();

  // Auto-redirect dacă ești deja logat
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Pre-fill referral code from sessionStorage (set by /invita/:code redirect)
  useEffect(() => {
    const stored = sessionStorage.getItem('referralCode');
    if (stored) {
      setReferralCode(stored);
      sessionStorage.removeItem('referralCode');
      // Auto-validate
      referralsService.validateCode(stored).then(res => {
        setReferralValid(res.valid);
        if (res.valid && res.referrerName) setReferralName(res.referrerName);
      }).catch(() => {});
    }
  }, []);

  const handleReferralBlur = async () => {
    const code = referralCode.trim().toUpperCase();
    if (!code) { setReferralValid(null); setReferralName(''); return; }
    try {
      const res = await referralsService.validateCode(code);
      setReferralValid(res.valid);
      setReferralName(res.valid && res.referrerName ? res.referrerName : '');
    } catch {
      setReferralValid(false);
      setReferralName('');
    }
  };

  // Form state — always PATIENT; specialists/centers use /business/register
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptNewsletter, setAcceptNewsletter] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState<boolean | null>(null);
  const [referralName, setReferralName] = useState('');

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState('Introdu o parolă');

  // Password strength checker
  useEffect(() => {
    if (password.length === 0) {
      setPasswordStrength(0);
      setPasswordFeedback('Introdu o parolă');
      return;
    }

    let strength = 0;
    const feedback: string[] = [];

    if (password.length >= 8) strength++;
    else feedback.push('minim 8 caractere');

    if (/[a-z]/.test(password)) strength++;
    else feedback.push('litere mici');

    if (/[A-Z]/.test(password)) strength++;
    else feedback.push('litere mari');

    if (/[\d\W]/.test(password)) strength++;
    else feedback.push('cifre sau simboluri');

    setPasswordStrength(strength);

    const texts = ['Foarte slabă', 'Slabă', 'Medie', 'Puternică'];
    if (strength > 0) {
      setPasswordFeedback(texts[strength - 1]);
    } else {
      setPasswordFeedback(`Lipsesc: ${feedback.join(', ')}`);
    }
  }, [password]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!acceptTerms) {
      setError('Trebuie să accepți termenii și condițiile.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Parolele nu se potrivesc.');
      return;
    }

    if (password.length < 8) {
      setError('Parola trebuie să aibă cel puțin 8 caractere.');
      return;
    }

    setLoading(true);

    try {
      const sanitizedEmail = email.trim().toLowerCase();
      const sanitizedPassword = password.trim();
      const sanitizedFirstName = firstName.trim();
      const sanitizedLastName = lastName.trim();

      await register(sanitizedEmail, sanitizedPassword, sanitizedFirstName, sanitizedLastName, 'PATIENT');

      // Apply referral code if provided and valid
      const codeToApply = referralCode.trim().toUpperCase();
      if (codeToApply && referralValid !== false) {
        try {
          await referralsService.applyCode(codeToApply);
        } catch {
          // Non-blocking - registration still succeeds
        }
      }

      // PATIENT: First select lifestyle mode (Healthy vs Chronic)
      navigate('/lifestyle-mode');
    } catch (err: any) {
      console.error('Register error:', err);
      setError(err.message || 'Eroare la înregistrare. Încearcă din nou.');
    } finally {
      setLoading(false);
    }
  };

  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500'];
  const strengthTextColors = ['text-red-600', 'text-orange-600', 'text-yellow-600', 'text-green-600'];

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-green-50 min-h-screen">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Dumbbell className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">FII FIT</span>
            </Link>

            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                Conectare
              </Link>
              <Link to="/" className="text-gray-600 hover:text-blue-600 transition-colors flex items-center">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Înapoi
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Creează-ți contul
            </h2>
            <p className="text-gray-600">
              Alătură-te comunității FII FIT și începe-ți transformarea fizică cu AI
            </p>
          </div>

          {/* Business Banner (web only: business plans are not offered in the iOS app) */}
          {!isIOS() && (
            <Link
              to="/business/register"
              className="flex items-center gap-3 p-4 bg-teal-50 border border-teal-200 rounded-xl hover:bg-teal-100 transition-all group"
            >
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-teal-800">Ești antrenor personal sau nutriționist?</p>
                <p className="text-xs text-teal-600">Înregistrează-te pe FII FIT Business →</p>
              </div>
              <ArrowLeft className="w-4 h-4 text-teal-500 rotate-180 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}

          {/* Registration Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Prenume
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Ion"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Nume
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Popescu"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Adresa de email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="exemplu@email.com"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  <Lock className="w-4 h-4 inline mr-2" />
                  Parola
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Minim 8 caractere"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {/* Password Strength Indicator */}
                <div className="mt-2">
                  <div className="flex space-x-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded ${
                          i < passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs mt-1 ${
                    passwordStrength > 0 ? strengthTextColors[passwordStrength - 1] : 'text-gray-500'
                  }`}>
                    {passwordFeedback}
                  </p>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  <ShieldCheck className="w-4 h-4 inline mr-2" />
                  Confirmă parola
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    confirmPassword && password !== confirmPassword
                      ? 'border-red-500'
                      : confirmPassword && password === confirmPassword
                      ? 'border-green-500'
                      : 'border-gray-300'
                  }`}
                  placeholder="Repetă parola"
                />
              </div>

              {/* Referral Code (optional) */}
              <div>
                <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-2">
                  <Gift className="w-4 h-4 inline mr-2" />
                  Cod de invitatie (optional)
                </label>
                <input
                  id="referralCode"
                  type="text"
                  value={referralCode}
                  onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralValid(null); setReferralName(''); }}
                  onBlur={handleReferralBlur}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono tracking-widest uppercase ${
                    referralValid === true
                      ? 'border-green-500 bg-green-50'
                      : referralValid === false
                      ? 'border-red-400 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  placeholder="Ex: RAZVAN83"
                  maxLength={20}
                />
                {referralValid === true && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Cod valid{referralName ? ` - invitat de ${referralName}` : ''}
                  </p>
                )}
                {referralValid === false && (
                  <p className="text-xs text-red-500 mt-1">Cod invalid sau inexistent</p>
                )}
              </div>

              {/* Terms & Consents */}
              <div className="space-y-4">
                {/* Terms checkbox */}
                <div className="flex items-start">
                  <input
                    id="terms"
                    type="checkbox"
                    required
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                  />
                  <label htmlFor="terms" className="ml-3 text-sm text-gray-700">
                    Sunt de acord cu{' '}
                    <Link to="/terms" target="_blank" className="text-blue-600 hover:text-blue-700 font-medium">
                      Termenii și Condițiile
                    </Link>
                    {' '}și{' '}
                    <Link to="/privacy" target="_blank" className="text-blue-600 hover:text-blue-700 font-medium">
                      Politica de Confidențialitate
                    </Link>
                    {' '}(obligatoriu) *
                  </label>
                </div>

                {/* Fitness Disclaimer */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>💪 Notă:</strong> FII FIT este un instrument de suport pentru fitness și nutriție sportivă.
                    Recomandările generate de AI au caracter informativ și sunt adaptate obiectivelor tale de fitness.
                  </p>
                </div>

                {/* Newsletter */}
                <div className="flex items-start">
                  <input
                    id="newsletter"
                    type="checkbox"
                    checked={acceptNewsletter}
                    onChange={(e) => setAcceptNewsletter(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                  />
                  <label htmlFor="newsletter" className="ml-3 text-sm text-gray-700">
                    Vreau să primesc newsletter-ul cu noutăți fitness și sfaturi de antrenament (opțional)
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <UserPlus className="w-5 h-5 inline mr-2" />
                {loading ? 'Se creează contul...' : 'Creează contul'}
              </button>
            </form>

            {/* Login Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Ai deja cont?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                  Conectează-te aici
                </Link>
              </p>
            </div>
          </div>

          {/* Security Notice */}
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <ShieldCheck className="w-4 h-4 mr-1 text-green-600" />
                SSL Securizat
              </div>
              <div className="flex items-center">
                <Lock className="w-4 h-4 mr-1 text-blue-600" />
                Date Criptate
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-1 text-purple-600" />
                GDPR Compliant
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
